import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { notification } from "../../utils/Notifications.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import S3Service from "../../services/s3.service.ts";
import {
  getPermissionById,
  approvePermission,
  getPermissionsByPartner,
} from "../../services/permissions.service.ts";
import { getScheduleByPartner } from "../../services/branch-schedule.service.ts";
import PermissionFormEditFiles from "../../components/permissions/PermissionFormEditFiles.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel, PanelHeader } from "../../components/panel/panel.jsx";
import Button from "../../components/Button.tsx";
import Loader from "../../components/Loader.tsx";
import moment from "moment";
import "moment/locale/es";

moment.locale("es");

const ApprovedPermission = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  useAppSettings();
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState(null);
  const [partnerSchedule, setPartnerSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [reloadFiles, setReloadFiles] = useState(false);
  const [profileUrl, setProfileUrl] = useState("");
  const [activeTab, setActiveTab] = useState('info');
  const [partnerHistory, setPartnerHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const isPartner = false;

  const validationSchema = Yup.object().shape({
    approved: Yup.boolean().required("Debe seleccionar una opción"),
    reviewerComment: Yup.string().when("approved", {
      is: false,
      then: (schema) =>
        schema.required("El comentario es obligatorio al rechazar"),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      description: "",
      approved: false,
      daysRequested: 1,
      startRequested: "",
      endRequested: "",
      requireTacticRelease: false,
      reviewerComment: "",
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const permissionData = {
          description: values.description,
          approved: values.approved,
          daysRequested: values.daysRequested,
          startRequested: values.startRequested
            ? dayjs(values.startRequested).unix()
            : undefined,
          endRequested: values.endRequested
            ? dayjs(values.endRequested).unix()
            : undefined,
          isRejected: !values.approved,
          requireTacticRelease: values.approved
            ? !!values.requireTacticRelease
            : false,
          reviewerComment: values.reviewerComment,
        };

        console.log("Enviando datos del permiso:", permissionData);

        await approvePermission(Number(id), permissionData);
        notification(
          "Éxito",
          `Permiso ${values.approved ? "aprobado" : "rechazado"} correctamente`,
          "success"
        );
        navigate(-1);
      } catch (error) {
        console.error("Error al procesar el permiso:", error);
        notification("Error", "No se pudo procesar el permiso", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchPermission = async () => {
    setLoading(true);
    try {
      const response = await getPermissionById(Number(id));
      setPermission(response);

      if (response.partner?.person?.curp) {
        try {
          const profileUrlResponse = await S3Service.getFile(
            "PERFIL_" + response.partner.person.curp
          );
          setProfileUrl(profileUrlResponse.url);
        } catch (error) {
          console.error("Error al obtener foto de perfil:", error);
          setProfileUrl("/assets/img/avatar.jpg");
        }
      }

      formik.setValues({
        description: response.description || "",
        approved: response.approved || false,
        daysRequested: response.daysRequested || 1,
        startRequested: response.startRequested
          ? dayjs.unix(response.startRequested).format("YYYY-MM-DD")
          : "",
        endRequested: response.endRequested
          ? dayjs.unix(response.endRequested).format("YYYY-MM-DD")
          : "",
        requireTacticRelease: !!response.requireTacticRelease,
        reviewerComment: response.reviewerComment || "",
      });
    } catch (error) {
      console.error("Error al cargar el permiso:", error);
      notification("Error", "No se pudo cargar el permiso", "danger");
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerSchedule = async () => {
    if (
      !permission?.partner?.id ||
      !permission?.startRequested ||
      !permission?.endRequested
    )
      return;

    setLoadingSchedule(true);
    try {
      const startDate = dayjs.unix(permission.startRequested);
      const endDate = dayjs.unix(permission.endRequested);

      const allPartnerSchedules = await getScheduleByPartner(
        permission.partner.id
      );

      const schedulesInRange =
        allPartnerSchedules.records?.filter((schedule) => {
          if (!schedule.exactDate) return false;

          const scheduleDate = dayjs(schedule.exactDate);
          const isInRange =
            scheduleDate.isSameOrAfter(startDate, "day") &&
            scheduleDate.isSameOrBefore(endDate, "day");

          return isInRange;
        }).sort((a, b) => {
          const dateA = dayjs(a.exactDate);
          const dateB = dayjs(b.exactDate);
          return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
        }) || [];

      setPartnerSchedule(schedulesInRange);
    } catch (error) {
      console.error("Error al cargar el horario del partner:", error);
      setPartnerSchedule(null);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const loadPartnerHistory = async (partnerId: number) => {
    try {
      setLoadingHistory(true);
      const history = await getPermissionsByPartner(partnerId);
      // Ordenar por fecha más reciente (inicio o creación)
      const sortedHistory = (history || []).sort((a, b) => {
        const dateA = a.startRequested
          ? dayjs.unix(a.startRequested)
          : a.createdAt
          ? dayjs.unix(a.createdAt)
          : dayjs(0);
        const dateB = b.startRequested
          ? dayjs.unix(b.startRequested)
          : b.createdAt
          ? dayjs.unix(b.createdAt)
          : dayjs(0);
        return dateB.diff(dateA);
      });
      setPartnerHistory(sortedHistory);
    } catch (error) {
      console.error("Error loading partner history:", error);
      setPartnerHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchPermission();
  }, [id]);

  useEffect(() => {
    if (permission) {
      fetchPartnerSchedule();
      if (permission.partner?.id) {
        loadPartnerHistory(permission.partner.id);
      }
    }
  }, [permission]);

  const handleReloadFiles = () => {
    setReloadFiles(!reloadFiles);
    fetchPermission();
  };

  const getPermissionStatus = () => {
    if (!permission) return "";
    if (permission.approved) return "APROBADO";
    if (permission.isRejected) return "RECHAZADO";
    return "PENDIENTE";
  };

  const getStatusColor = () => {
    if (!permission) return "bg-secondary";
    if (permission.approved) return "bg-success";
    if (permission.isRejected) return "bg-danger";
    return "bg-warning";
  };

  const getHistoryStatusColor = (permissionItem: any) => {
    if (permissionItem.approved) return "bg-success";
    if (permissionItem.isRejected) return "bg-danger";
    return "bg-warning";
  };

  const getHistoryStatus = (permissionItem: any) => {
    if (permissionItem.approved) return "APROBADO";
    if (permissionItem.isRejected) return "RECHAZADO";
    return "PENDIENTE";
  };

  const isAlreadyProcessed =
    permission && (permission.approved || permission.isRejected);

  const formatScheduleTime = (time: string) => {
    if (!time) return "-";

    return time.split(" - ")[0] || time;
  };

  const formatScheduleDate = (date: string) => {
    if (!date) return "-";
    return moment(date).format("dddd, D [de] MMMM [de] YYYY");
  };

  if (!permission) {
    return (
      <Layout>
        <Loader isLoading={loading} />
      </Layout>
    );
  }

  return (
    <Layout>
      <Title
        baseTitle="Revisar permiso"
        basePath={`/colaboradores/aprobar-permiso/${id}`}
        title="Revisar y procesar permiso"
      />

      <div className="m-4">
        <div className="row">
          <div className="col-lg-4 mb-3">
            {permission?.partner && (
              <>
              {/* Tabs: Información / Historial */}
              <div
                className="d-flex shadow-sm mb-3 border"
                style={{
                  borderRadius: "0.5rem",
                  overflow: "hidden",
                  backgroundColor: "#ffffff",
                  borderColor: "#e5e7eb",
                }}
              >
                <button
                  type="button"
                  className="flex-fill border-0 py-2 px-3 d-flex align-items-center justify-content-center small fw-semibold"
                  style={{
                    backgroundColor:
                      activeTab === "info" ? "#50B7C0" : "#ffffff",
                    color: activeTab === "info" ? "#ffffff" : "#4B5563",
                    borderRadius: "0.4rem 0 0 0.4rem",
                    transition: "background-color 0.15s ease, color 0.15s ease",
                  }}
                  onClick={() => setActiveTab("info")}
                >
                  <i
                    className="fas fa-user me-1"
                    style={{ opacity: activeTab === "info" ? 1 : 0.7 }}
                  ></i>
                  Información
                </button>
                <button
                  type="button"
                  className="flex-fill border-0 py-2 px-3 d-flex align-items-center justify-content-center small fw-semibold"
                  style={{
                    backgroundColor:
                      activeTab === "history" ? "#50B7C0" : "#ffffff",
                    color: activeTab === "history" ? "#ffffff" : "#4B5563",
                    borderRadius: "0 0.4rem 0.4rem 0",
                    transition: "background-color 0.15s ease, color 0.15s ease",
                  }}
                  onClick={() => setActiveTab("history")}
                >
                  <i
                    className="fas fa-history me-1"
                    style={{ opacity: activeTab === "history" ? 1 : 0.7 }}
                  ></i>
                  Historial
                </button>
              </div>

              {activeTab === "info" && (
                <>
                  {/* Card 1: Información del Chilito */}
                  <div
                    className="card border-0 shadow-sm overflow-hidden mb-3"
                    style={{ borderRadius: "1rem" }}
                  >
                    <div
                      className="py-2 px-3 text-white d-flex align-items-center"
                      style={{ backgroundColor: "#50B7C0" }}
                    >
                      <i className="fas fa-user me-2"></i>
                      <h6 className="mb-0 fw-semibold">Información del Chilito</h6>
                    </div>
                    <div className="card-body p-4">
                      <div className="text-center mb-4">
                        <img
                          src={profileUrl || "/assets/img/avatar.jpg"}
                          alt="Foto de perfil"
                          className="rounded-circle shadow"
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            border: "3px solid #e5e7eb",
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "/assets/img/avatar.jpg";
                          }}
                        />
                        <h5 className="mt-3 mb-0 fw-bold text-dark">
                          {toCamelCase(
                            `${permission.partner.person?.firstName} ${permission.partner.person?.lastName} ${permission.partner.person?.secondLastName}`
                          )}
                        </h5>
                      </div>
                      <div className="border-top pt-3">
                        <div className="d-flex align-items-center mb-3">
                          <span className="text-muted small me-2" style={{ minWidth: "80px" }}>
                            <i className="fas fa-briefcase me-1" style={{ color: "#50B7C0" }}></i>
                            Puesto
                          </span>
                          <span className="fw-semibold text-dark small">
                            {permission.partner.candidate?.vacancy?.position?.name || "—"}
                          </span>
                        </div>
                        <div className="d-flex align-items-center mb-3">
                          <span className="text-muted small me-2" style={{ minWidth: "80px" }}>
                            <i className="fas fa-building me-1" style={{ color: "#50B7C0" }}></i>
                            Sucursal
                          </span>
                          <span className="fw-semibold text-dark small">
                            {permission.partner.candidate?.branch?.name || "—"}
                          </span>
                        </div>
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pt-2 border-top">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-id-card me-2 text-muted small"></i>
                            <span className="small text-dark fw-medium">
                              NSS {permission.partner.person?.nss || "No registrado"}
                            </span>
                          </div>
                          {permission.partner.hasChiliLifeInsurance != null && (
                            <span
                              className="badge small fw-normal"
                              style={{
                                backgroundColor: permission.partner.hasChiliLifeInsurance ? "#d1fae5" : "#f3f4f6",
                                color: permission.partner.hasChiliLifeInsurance ? "#065f46" : "#4b5563",
                                borderRadius: "999px",
                                padding: "0.35rem 0.75rem",
                              }}
                            >
                              Seguro Chili: {permission.partner.hasChiliLifeInsurance ? "Sí" : "No"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Horarios para el período */}
                  <div
                    className="card border-0 shadow-sm overflow-hidden mb-3"
                    style={{ borderRadius: "1rem" }}
                  >
                    <div className="card-body p-4">
                      <h6 className="mb-3 text-dark d-flex align-items-center">
                        <i className="fas fa-clock me-2" style={{ color: "#50B7C0" }}></i>
                        Horarios para el período{" "}
                        {permission.startRequested && permission.endRequested
                          ? `${dayjs.unix(permission.startRequested).format("DD/MM/YYYY")} - ${dayjs.unix(permission.endRequested).format("DD/MM/YYYY")}`
                          : ""}
                      </h6>

                      {loadingSchedule ? (
                        <div className="text-center py-4">
                          <div className="spinner-border spinner-border-sm text-secondary" role="status">
                            <span className="visually-hidden">Cargando horario...</span>
                          </div>
                          <div className="text-muted small mt-2">Verificando horarios...</div>
                        </div>
                      ) : partnerSchedule && partnerSchedule.length > 0 ? (
                        <div>
                          <div
                            className="p-3 rounded-3 small mb-3"
                            style={{ backgroundColor: "#fef3c7", color: "#92400e" }}
                          >
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            <strong>Atención:</strong> El colaborador tiene horarios asignados para{" "}
                            {permission.startRequested && permission.endRequested && permission.startRequested !== permission.endRequested
                              ? "algunas fechas del período solicitado"
                              : "la fecha solicitada"}.
                            {formik.values.requireTacticRelease &&
                              " Al aprobar este permiso, será eliminado automáticamente de estos horarios."}
                          </div>
                          <div
                            className="rounded-3 overflow-auto"
                            style={{
                              maxHeight: "220px",
                              border: "1px solid #e5e7eb",
                              backgroundColor: "#f9fafb",
                            }}
                          >
                            <div className="p-3">
                              {partnerSchedule.map((schedule, index) => (
                                <div
                                  key={index}
                                  className="bg-white rounded-3 p-3 mb-2 shadow-sm border-start border-3"
                                  style={{ borderLeftColor: "#50B7C0" }}
                                >
                                  <div className="row align-items-center small">
                                    <div className="col-12 col-md-6 mb-2 mb-md-0">
                                      <div className="fw-semibold text-dark">
                                        {schedule.positionName || "Sin posición"}
                                      </div>
                                      <div className="text-muted">
                                        {schedule.dayOfWeek && schedule.exactDate
                                          ? `${schedule.dayOfWeek} · ${dayjs(schedule.exactDate).format("DD/MM/YYYY")}`
                                          : schedule.branch?.name || "Sin sucursal"}
                                      </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                      <div className="text-muted small">Horario</div>
                                      <div className="text-dark">
                                        {formatScheduleTime(schedule.startTime)} – {formatScheduleTime(schedule.endTime)}
                                      </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                      <div className="text-muted small">Turno</div>
                                      <div className="text-dark">{schedule.shift || "Sin turno"}</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          {partnerSchedule.length > 2 && (
                            <div className="text-center mt-2">
                              <small className="text-muted">
                                <i className="fas fa-info-circle me-1"></i>
                                {partnerSchedule.length} horarios – desplázate para ver todos
                              </small>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          className="p-3 rounded-3 small"
                          style={{ backgroundColor: "#d1fae5", color: "#065f46" }}
                        >
                          <i className="fas fa-check-circle me-2"></i>
                          <strong>Sin conflictos:</strong> El colaborador no tiene horarios asignados para esta fecha.
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Historial Tab */}
              {activeTab === "history" && (
                <div
                  className="card border-0 shadow-sm overflow-hidden mb-3"
                  style={{ borderRadius: "1rem" }}
                >
                  <div
                    className="py-2 px-3 text-white d-flex align-items-center"
                    style={{ backgroundColor: "#50B7C0" }}
                  >
                    <i className="fas fa-history me-2"></i>
                    <h6 className="mb-0 fw-semibold">Historial de permisos</h6>
                  </div>
                  <div className="card-body p-4">
                    {loadingHistory ? (
                      <div className="d-flex flex-column align-items-center py-4">
                        <div className="spinner-border text-secondary" role="status">
                          <span className="visually-hidden">Cargando...</span>
                        </div>
                        <div className="text-muted small mt-2">Cargando historial...</div>
                      </div>
                    ) : partnerHistory.length > 0 ? (
                      <div className="overflow-auto" style={{ maxHeight: "400px" }}>
                        {partnerHistory.map((item: any, index: number) => (
                          <div
                            key={item.id || index}
                            className="card mb-2 border-0 shadow-sm"
                            style={{ borderRadius: "0.75rem", minHeight: "80px" }}
                          >
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="fw-semibold text-dark small">
                                  {item.description
                                    ? item.description.length > 30
                                      ? item.description.substring(0, 30) + "..."
                                      : item.description
                                    : "Sin descripción"}
                                </div>
                                <span className={`badge ${getHistoryStatusColor(item)}`} style={{ borderRadius: "999px" }}>
                                  {getHistoryStatus(item)}
                                </span>
                              </div>
                              <div className="text-muted small mb-1">
                                <i className="fas fa-calendar me-1"></i>
                                {item.startRequested ? dayjs.unix(item.startRequested).format("DD/MM/YYYY") : "-"}
                              </div>
                              <div className="text-muted small mb-1">
                                <i className="fas fa-clock me-1"></i>
                                {item.daysRequested ?? 1} día{(item.daysRequested ?? 1) > 1 ? "s" : ""}
                              </div>
                              {item.reviewerComment && (
                                <div className="text-muted small">
                                  <i className="fas fa-comment me-1"></i>
                                  {item.reviewerComment.length > 40
                                    ? item.reviewerComment.substring(0, 40) + "..."
                                    : item.reviewerComment}
                                </div>
                              )}
                              {item.id === permission.id && (
                                <div className="mt-2">
                                  <span className="badge bg-info">Permiso actual</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="fas fa-inbox fa-2x mb-2"></i>
                        <div>No hay historial de permisos</div>
                        <small>Este colaborador no tiene registros previos</small>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="col-lg-8">
          <div
            className="card border-0 shadow-sm mb-4"
            style={{ borderRadius: "1rem", overflow: "hidden" }}
          >
            <Panel>
              <PanelHeader noButton={true} className="bg-azul">
                Datos generales del permiso
              </PanelHeader>
              <div className="p-4">
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Descripción</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={permission.description || ""}
                      disabled
                    />
                  </div>

                  <div className="col-md-5 mb-3">
                    <label className="form-label">Fecha de Inicio</label>
                    <input
                      type="date"
                      className="form-control"
                      value={
                        permission.startRequested
                          ? dayjs
                              .unix(permission.startRequested)
                              .format("YYYY-MM-DD")
                          : ""
                      }
                      disabled
                    />
                  </div>

                  <div className="col-md-5 mb-3">
                    <label className="form-label">Fecha de Fin</label>
                    <input
                      type="date"
                      className="form-control"
                      value={
                        permission.endRequested
                          ? dayjs
                              .unix(permission.endRequested)
                              .format("YYYY-MM-DD")
                          : ""
                      }
                      disabled
                    />
                  </div>

                  <div className="col-md-2 mb-3">
                    <label className="form-label">Días Solicitados</label>
                    <input
                      type="number"
                      className="form-control"
                      value={permission.daysRequested || 1}
                      disabled
                    />
                  </div>

                  {!isAlreadyProcessed && (
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Decisión *</label>
                      <div className="mt-2">
                        <div className="form-check form-check-inline">
                          <input
                            type="radio"
                            name="approved"
                            value="true"
                            className="form-check-input"
                            id="approve"
                            checked={formik.values.approved === true}
                            onChange={() =>
                              formik.setFieldValue("approved", true)
                            }
                          />
                          <label className="form-check-label" htmlFor="approve">
                            Aprobar
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            type="radio"
                            name="approved"
                            value="false"
                            className="form-check-input"
                            id="reject"
                            checked={formik.values.approved === false}
                            onChange={() =>
                              formik.setFieldValue("approved", false)
                            }
                          />
                          <label className="form-check-label" htmlFor="reject">
                            Rechazar
                          </label>
                        </div>
                      </div>
                      {formik.touched.approved && formik.errors.approved && (
                        <div className="text-danger small mt-1">
                          {formik.errors.approved}
                        </div>
                      )}
                    </div>
                  )}

                  {!isAlreadyProcessed && formik.values.approved === true && (
                    <div className="col-md-12 mb-3">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          id="requireTacticRelease"
                          className="form-check-input"
                          checked={!!formik.values.requireTacticRelease}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "requireTacticRelease",
                              e.target.checked
                            )
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="requireTacticRelease"
                        >
                          ¿Es necesario liberar esta posición en la táctica de
                          juego para que sea cubierta por otra persona
                          (colaborador)?
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Comentario del revisor{" "}
                      {formik.values.approved === false && (
                        <span className="text-danger">*</span>
                      )}
                    </label>
                    <textarea
                      className={`form-control ${
                        formik.touched.reviewerComment &&
                        formik.errors.reviewerComment
                          ? "is-invalid"
                          : ""
                      }`}
                      rows="3"
                      placeholder={"Comentario del revisor"}
                      value={formik.values.reviewerComment}
                      onChange={(e) =>
                        formik.setFieldValue("reviewerComment", e.target.value)
                      }
                      onBlur={formik.handleBlur}
                      name="reviewerComment"
                      disabled={isAlreadyProcessed}
                    />
                    {formik.touched.reviewerComment &&
                      formik.errors.reviewerComment && (
                        <div className="invalid-feedback">
                          {formik.errors.reviewerComment}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </Panel>
          </div>

          <div
            className="card border-0 shadow-sm"
            style={{ borderRadius: "1rem", overflow: "hidden" }}
          >
            <Panel>
              <PanelHeader noButton={true} className="bg-azul">
                Archivos adjuntos del permiso
              </PanelHeader>
              <PermissionFormEditFiles
                permission={permission}
                reloadFiles={handleReloadFiles}
                isDisabled={isAlreadyProcessed}
              />
            </Panel>
          </div>
        </div>
        </div>
      </div>

      <div className="card border-0 m-4 rounded p-3">
        <div className="row justify-content-end">
          <div className="col-md-3">
            <Button
              onClick={() => navigate(-1)}
              title="Cancelar"
              variant="secondary"
              type="button"
              disabled={loading}
            />
          </div>
          {!isAlreadyProcessed ? (
            <div className="col-md-3">
              <Button
                onClick={() => formik.handleSubmit()}
                title="Procesar"
                variant="azul"
                disabled={loading}
                type="submit"
              />
            </div>
          ) : (
            <div className="col-md-3">
              <Button
                onClick={() => navigate(-1)}
                title={`Permiso ${getPermissionStatus().toLowerCase()}`}
                variant="secondary"
                type="button"
                disabled={true}
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ApprovedPermission;
